// dxfExport.ts — DXF writer for panel and site plan export.
// Built on @tarikjabiri/dxf (CV-825): the previous hand-rolled R2000 writer
// produced files AC2027's DXFIN rejected (dim subclass/group-code drift).
// The library emits AC1021 with complete tables; verified via headless
// accoreconsole DXFIN (entity count + zero errors).
// All values in inches to match v11 coordinate system.

import { DxfWriter, Units, point3d } from '@tarikjabiri/dxf';
import type { PanelData, CVProject } from '../types/panelvar';
import { outlineFromMp } from './panelGeometry';

// ── Layer definitions (mirror v11 drawpan.lsp layer names) ───────────────────
const LAYERS = [
  { name: 'PANEL',       color: 7,  ltype: 'Continuous' },   // white — panel outline
  { name: 'OPENINGS',    color: 4,  ltype: 'Continuous' },   // cyan  — windows/doors
  { name: 'BLOCKOUTS',   color: 3,  ltype: 'Continuous' },   // green — blockouts
  { name: 'FEATURES',    color: 5,  ltype: 'Continuous' },   // blue  — reveals/chamfers
  { name: 'STRUCTURAL',  color: 1,  ltype: 'Continuous' },   // red   — brace/pick/weld
  { name: 'DIMENSIONS',  color: 2,  ltype: 'Continuous' },   // yellow
  { name: 'NOTES',       color: 7,  ltype: 'Continuous' },
  { name: 'REVISIONS',   color: 6,  ltype: 'Continuous' },   // magenta
  { name: 'SITE',        color: 7,  ltype: 'Continuous' },
  { name: 'GRID',        color: 8,  ltype: 'DASHED' },
];

function newWriter(): DxfWriter {
  const dxf = new DxfWriter();
  dxf.setUnits(Units.Inches);
  dxf.addLType('DASHED', '__ __ __ __ __', [0.5, -0.25]);
  for (const l of LAYERS) dxf.addLayer(l.name, l.color, l.ltype);
  return dxf;
}

function line(dxf: DxfWriter, x1: number, y1: number, x2: number, y2: number, layer: string): void {
  dxf.addLine(point3d(x1, y1, 0), point3d(x2, y2, 0), { layerName: layer });
}

function rect(dxf: DxfWriter, x: number, y: number, w: number, h: number, layer: string): void {
  dxf.addLWPolyline(
    [
      { point: { x,         y } },
      { point: { x: x + w,  y } },
      { point: { x: x + w,  y: y + h } },
      { point: { x,         y: y + h } },
    ],
    { flags: 1, layerName: layer },
  );
}

function circle(dxf: DxfWriter, cx: number, cy: number, r: number, layer: string): void {
  dxf.addCircle(point3d(cx, cy, 0), r, { layerName: layer });
}

function text(dxf: DxfWriter, x: number, y: number, height: number, value: string, layer: string, angle = 0): void {
  dxf.addText(point3d(x, y, 0), height, value, { layerName: layer, rotation: angle });
}

function dimLinear(dxf: DxfWriter, x1: number, y1: number, x2: number, y2: number, dimAt: number, layer: string): void {
  const horiz = y1 === y2;
  // offset = signed distance from the measured points to the dimension line.
  const offset = horiz ? dimAt - y1 : dimAt - x1;
  dxf.addLinearDim(point3d(x1, y1, 0), point3d(x2, y2, 0), {
    offset,
    angle: horiz ? 0 : 90,
    layerName: layer,
  });
}

function titleBlock(dxf: DxfWriter, panel: PanelData): void {
  const mp = panel.mpvar;
  const tbX = 0, tbY = -48;  // Title block below panel
  rect(dxf, tbX, tbY, 120, 48, 'NOTES');  // outer border
  // Dividers
  line(dxf, tbX, tbY + 24, tbX + 120, tbY + 24, 'NOTES');
  line(dxf, tbX + 60, tbY, tbX + 60, tbY + 24, 'NOTES');
  // Labels
  text(dxf, tbX + 2, tbY + 38, 3, 'PROJECT: ' + mp.projectName, 'NOTES');
  text(dxf, tbX + 2, tbY + 32, 3, 'DRAWING: ' + mp.drawingNumber, 'NOTES');
  text(dxf, tbX + 62, tbY + 38, 3, 'MARK: ' + mp.panelMark, 'NOTES');
  text(dxf, tbX + 62, tbY + 32, 3, 'DATE: ' + mp.date, 'NOTES');
  text(dxf, tbX + 2, tbY + 18, 3, 'DWN: ' + mp.drawBy, 'NOTES');
  text(dxf, tbX + 62, tbY + 18, 3, 'CHK: ' + mp.checkBy, 'NOTES');
  text(dxf, tbX + 2, tbY + 10, 3, 'REV: ' + mp.revision, 'NOTES');
  text(dxf, tbX + 62, tbY + 10, 3, 'THKNS: ' + mp.thickness + '"', 'NOTES');
}

/** Build a panel DXF document as a string. Throws if dimensions are invalid. */
export function buildPanelDXFString(panel: PanelData): string {
  const mp  = panel.mpvar;
  const w   = mp.width;
  const h   = mp.height;

  if (!w || !h || w <= 0 || h <= 0) {
    throw new Error(`Cannot export DXF: panel "${mp.panelMark || 'unnamed'}" has invalid dimensions (${w}" × ${h}").`);
  }

  const dxf = newWriter();

  // ── Panel outline (actual top shape polygon) ───────────────────────────────
  const outline = outlineFromMp(mp);
  dxf.addLWPolyline(
    outline.vertices.map(v => ({ point: { x: v.x, y: v.y } })),
    { flags: 1, layerName: 'PANEL' },
  );

  // ── Width + height dimensions ──────────────────────────────────────────────
  dimLinear(dxf, 0, 0, w, 0, -12, 'DIMENSIONS');
  dimLinear(dxf, w, 0, w, h, w + 12, 'DIMENSIONS');

  // ── Panel mark label ────────────────────────────────────────────────────────
  text(dxf, w / 2 - 6, h / 2, 6, mp.panelMark, 'NOTES');

  // ── Rough openings ─────────────────────────────────────────────────────────
  panel.rovar.forEach((slot) => {
    if (!slot.enabled) return;
    const d = slot.data;
    rect(dxf, d.x - d.width / 2, d.y - d.height / 2, d.width, d.height, 'OPENINGS');
    text(dxf, d.x, d.y, 3, d.mark || 'RO', 'NOTES');
  });

  // ── Standard openings (windows) ───────────────────────────────────────────
  panel.wdvar.forEach((slot) => {
    if (!slot.enabled) return;
    const d = slot.data;
    rect(dxf, d.x - d.width / 2, d.y - d.height / 2, d.width, d.height, 'OPENINGS');
    text(dxf, d.x, d.y + d.height / 2 + 3, 2.5, d.mark || 'W', 'NOTES');
  });

  // ── Man doors ─────────────────────────────────────────────────────────────
  panel.drvar.forEach((slot) => {
    if (!slot.enabled) return;
    const d = slot.data;
    rect(dxf, d.x - d.width / 2, d.y, d.width, d.height, 'OPENINGS');
    text(dxf, d.x, d.height / 2, 2.5, d.mark || 'D', 'NOTES');
  });

  // ── Dock levelers ─────────────────────────────────────────────────────────
  panel.dlvar.forEach((slot) => {
    if (!slot.enabled) return;
    const d = slot.data;
    rect(dxf, d.x - d.width / 2, d.y - d.height / 2, d.width, d.height, 'BLOCKOUTS');
    text(dxf, d.x, d.y, 2.5, d.mark || 'DL', 'NOTES');
  });

  // ── Rectangular blockouts ─────────────────────────────────────────────────
  panel.sbvar.forEach((slot) => {
    if (!slot.enabled) return;
    const d = slot.data;
    rect(dxf, d.x - d.width / 2, d.y - d.height / 2, d.width, d.height, 'BLOCKOUTS');
    text(dxf, d.x, d.y, 2.5, d.mark || 'SB', 'NOTES');
  });

  // ── Round blockouts ───────────────────────────────────────────────────────
  panel.rbvar.forEach((slot) => {
    if (!slot.enabled) return;
    const d = slot.data;
    circle(dxf, d.x, d.y, d.diameter / 2, 'BLOCKOUTS');
    text(dxf, d.x, d.y, 2.5, d.mark || 'RB', 'NOTES');
  });

  // ── Horizontal feature strips ──────────────────────────────────────────────
  panel.fhvar.forEach((slot) => {
    if (!slot.enabled) return;
    const d = slot.data;
    const xS = d.xEnd > 0 ? d.xStart : 0;
    const xE = d.xEnd > 0 ? d.xEnd   : w;
    rect(dxf, xS, d.y, xE - xS, d.height, 'FEATURES');
  });

  // ── Vertical feature strips ────────────────────────────────────────────────
  panel.fvvar.forEach((slot) => {
    if (!slot.enabled) return;
    const d = slot.data;
    const yS = d.yEnd > 0 ? d.yStart : 0;
    const yE = d.yEnd > 0 ? d.yEnd   : h;
    rect(dxf, d.x - d.width / 2, yS, d.width, yE - yS, 'FEATURES');
  });

  // ── Brace points ──────────────────────────────────────────────────────────
  panel.bpvar.forEach((slot) => {
    if (!slot.enabled) return;
    circle(dxf, slot.data.x, slot.data.y, 3, 'STRUCTURAL');
    text(dxf, slot.data.x + 4, slot.data.y, 2.5, slot.data.mark || 'BP', 'NOTES');
  });

  // ── Pick points ───────────────────────────────────────────────────────────
  panel.ppvar.forEach((slot) => {
    if (!slot.enabled) return;
    circle(dxf, slot.data.x, slot.data.y, 4, 'STRUCTURAL');
    text(dxf, slot.data.x + 5, slot.data.y, 2.5, slot.data.mark || 'PP', 'NOTES');
  });

  // ── Title block ───────────────────────────────────────────────────────────
  titleBlock(dxf, panel);

  return dxf.stringify();
}

export function panelDXFFilename(panel: PanelData): string {
  return `${panel.mpvar.panelMark || 'panel'}.dxf`;
}

/** Build a site DXF document as a string. Throws if site is not configured. */
export function buildSiteDXFString(project: CVProject): string {
  if (!project.site) {
    throw new Error('No site plan configured.');
  }
  const site = project.site;
  const scale = 12; // 1ft = 12 DXF units (inches scale)
  const dxf = newWriter();

  site.panels.forEach((placement) => {
    const panel = project.panels.find(p => p.id === placement.panelId);
    if (!panel) return;
    const mp = panel.mpvar;
    const cx = placement.x * scale;
    const cy = placement.y * scale;
    const pw = mp.width / 2;
    const pt = mp.thickness / 2;
    rect(dxf, cx - pw, cy - pt, mp.width, mp.thickness, 'SITE');
    text(dxf, cx - 6, cy - 1.5, 3, mp.panelMark, 'NOTES');
  });

  // Grid lines
  const size = 600;
  for (let i = 0; i <= size; i += site.gridSpacing * scale) {
    line(dxf, i, 0, i, size, 'GRID');
    line(dxf, 0, i, size, i, 'GRID');
  }

  return dxf.stringify();
}

export function siteDXFFilename(project: CVProject): string {
  const site = project.site;
  const base = (site?.projectName || project.name || 'site').replace(/\s+/g, '_');
  return `${base}_site.dxf`;
}
